import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AddIcon from '../../components/icons/AddIcon';
import EyeIcon from '../../components/icons/EyeIcon'; // New icon
import PencilFillIcon from '../../components/icons/PencilFillIcon';
import CancelContractIcon from '../../components/icons/CancelContractIcon'; // New icon
import PaginationArrowLeftIcon from '../../components/icons/PaginationArrowLeftIcon';
import PaginationArrowRightIcon from '../../components/icons/PaginationArrowRightIcon';
import SearchIcon from '../../components/icons/SearchIcon'; // Import SearchIcon
import DynamicTable from '../../components/Table/DynamicTable'; // Import the new DynamicTable component
import Pagination from '../../components/Pagination/Pagination'; // Import the Pagination component

const ContractListAdminScreen = () => {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');


    // Options for the status filter
    const statusOptions = [
        { value: "", label: "Filtrar por estado" },
        { value: "sent_to_client", label: "Por firmar (cliente)" },
        { value: "sent_to_agent", label: "Por firmar (agente)" },
        { value: "active", label: "Activo" },
        { value: "discontinued", label: "Descontinuado" },
        { value: "cancelled", label: "Cancelado" },
    ];


    // Example data for table rows (replace with actual data fetching logic)
    const contracts = [
        {
            id: 'CONTR-PREM-0724',
            clientName: 'Juan Pérez Morales',
            scheduleDate: '2024-07-05', // Example: Can be searched as text
            travelDateRange: '2024-08-01 a 2024-08-10',
            itineraryCode: 'ITN-0724-JP',
            totalAmount: '$150.000 CLP',
            status: 'active', // Changed to value from statusOptions
            statusClass: 'bg-primary'
        },
        {
            id: 'CONTR-PREM-0725',
            clientName: 'Juan Pérez Morales',
            scheduleDate: '2024-07-05',
            travelDateRange: '2024-08-01 a 2024-08-10', // Example: Can be searched as text
            itineraryCode: 'ITN-0724-JP',
            totalAmount: '$150.000 CLP',
            status: 'active',
            statusClass: 'bg-primary'
        },
        {
            id: 'CONTR-PREM-0726',
            clientName: 'Juan Pérez Morales',
            scheduleDate: '2024-07-05',
            travelDateRange: '2024-08-01 a 2024-08-10', // Example: Can be searched as text
            itineraryCode: 'ITN-0724-JP',
            totalAmount: '$150.000 CLP',
            status: 'active',
            statusClass: 'bg-primary'
        },
        {
            id: 'CONTR-PREM-0727',
            clientName: 'Juan Pérez Morales',
            scheduleDate: '2024-07-05',
            travelDateRange: '2024-08-01 a 2024-08-10', // Example: Can be searched as text
            itineraryCode: 'ITN-0724-JP',
            totalAmount: '$150.000 CLP',
            status: 'cancelled',
            statusClass: 'bg-danger'
        },
        {
            id: 'CONTR-PREM-0728',
            clientName: 'Juan Pérez Morales',
            scheduleDate: '2024-07-05',
            travelDateRange: '2024-08-01 a 2024-08-10', // Example: Can be searched as text
            itineraryCode: 'ITN-0724-JP',
            totalAmount: '$150.000 CLP',
            status: 'sent_to_client',
            statusClass: 'bg-info'
        },
        {
            id: 'CONTR-PREM-0729',
            clientName: 'Juan Pérez Morales',
            scheduleDate: '2024-07-05',
            travelDateRange: '2024-08-01 a 2024-08-10', // Example: Can be searched as text
            itineraryCode: 'ITN-0724-JP',
            totalAmount: '$150.000 CLP',
            status: 'sent_to_agent',
            statusClass: 'bg-info'
        },
    ];

    // Define columns for the DynamicTable
    const contractColumns = [
        { header: 'Código Contrato', accessor: 'id' },
        { header: 'Nombre Cliente', accessor: 'clientName' },
        { header: 'Fecha Agendamiento', accessor: 'scheduleDate' },
        { header: 'Fecha de Viaje (Rango)', accessor: 'travelDateRange' },
        {
            header: 'Itinerario',
            render: (contract) => (
                <Link to={`/itinerary/${contract.itineraryCode}`} data-bs-toggle="tooltip" data-bs-placement="top" title="Ver Itinerario">
                    {contract.itineraryCode}
                </Link>
            )
        },
        { header: 'Monto Total', accessor: 'totalAmount' },
        {
            header: 'Estado',
            render: (contract) => (
                // Look up the label from statusOptions based on the contract's status value
                <span className={`badge rounded-pill ${contract.statusClass}`}>
                    {statusOptions.find(option => option.value === contract.status)?.label || contract.status}</span>
            )
        },
    ];

    // Pagination state and configuration

    // Define actions for the DynamicTable
    const contractActions = [
        {
            icon: <EyeIcon />,
            tooltip: 'Ver Detalle del Contrato',
            className: 'btn-outline-info-checkin',
            onClick: (contract) => console.log('View contract:', contract.id) // Replace with actual navigation/modal logic
        },
        {
            icon: <PencilFillIcon />,
            tooltip: 'Editar Contrato',
            className: 'btn-outline-warning-checkin',
            onClick: (contract) => navigate(`/contracts/edit/${contract.id}`)
        },
        {
            icon: <CancelContractIcon />,
            tooltip: 'Cancelar Contrato',
            className: 'btn-outline-danger-checkin',
            onClick: (contract) => console.log('Cancel contract:', contract.id) // Replace with actual cancellation logic
        },
    ];

    // Define which columns are searchable for the "Buscar evaluaciones" input
    // You can customize this array to include/exclude columns as needed.
    // For example, if you only want to search by 'id' and 'clientName':
    // const searchableColumns = ['id', 'clientName'];
    const searchableColumns = ['id', 'clientName', 'scheduleDate', 'travelDateRange', 'itineraryCode', 'totalAmount'];

    // Apply search and filter logic
    const filteredContracts = contracts.filter(contract => {
        // 1. Filter by search term across specified columns
        const matchesSearchTerm = searchTerm.trim() === '' ||
            searchableColumns.some(column => {
                const value = String(contract[column]).toLowerCase();
                return value.includes(searchTerm.toLowerCase());
            });

        // 2. Filter by status
        const matchesStatus = filterStatus === '' || contract.status === filterStatus;

        return matchesSearchTerm && matchesStatus;
    });

    const totalItems = filteredContracts.length; // Total items for pagination is now based on filtered data, defined first

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; // Configurable:  Number of items per page
    const totalPages = Math.ceil(totalItems / itemsPerPage); // Now totalItems is defined

    // Calculate the start and end index for the current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedContracts = filteredContracts.slice(startIndex, endIndex);

    // Reset current page when search term or filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus]);

    return (
        <>
            {/* Header section with title, description, and "Create New Contract" button */}
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-3">
                <div className="pt-3 pb-2">
                    <h1 className="h2 mb-3">Gestión de Contratos</h1>
                    <p className="mb-0 text-muted text-start">Listado y administración de los contratos de los clientes.</p>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0 mt-3 mt-md-0"> {/* Added mt-3 mt-md-0 for spacing */}
                    <Link to="/contracts/new" className="btn btn-success d-flex align-items-center">
                        <AddIcon />
                        Crear Nuevo Contrato
                    </Link>
                </div>
            </div>
            <div className="row g-3 mb-3 align-items-center d-flex justify-content-between">
                <div className="col-md-8">
                    {/* Search Bar using standard input to ensure correct Bootstrap group styling */}
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar evaluaciones..."
                            aria-label="Buscar evaluaciones"
                            aria-describedby="button-search-evaluation"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="btn btn-outline-secondary" type="button" id="button-search-evaluation">
                            <SearchIcon />
                        </button>
                    </div>
                </div>
                <div className="col-md-4">
                    {/* Replaced SelectInput with native select for Bootstrap compatibility */}
                    <select
                        className="form-select"
                        id="filtroTipoEvaluacion"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <div className="card-body px-md-2 px-0">
                            {/* Use the new DynamicTable component here */}
                            <DynamicTable
                                data={paginatedContracts}
                                columns={contractColumns}
                                actions={contractActions}
                                keyAccessor="id" // Specify the unique key for each contract
                            />
                            {totalPages > 1 && (
                                <Pagination
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={totalItems}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContractListAdminScreen;